import { useState } from "react";
import type { FormEvent } from "react";
import { supabase } from "@/lib/supabase";
import { useStats } from "@/hooks/useStats";
import type { Stat } from "@/lib/types";

const inputClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-400 transition";

const iconOptions = [
  "ri-heart-3-line",
  "ri-restaurant-line",
  "ri-hand-heart-line",
  "ri-group-line",
  "ri-user-heart-line",
  "ri-shield-check-line",
  "ri-flashlight-line",
  "ri-map-pin-line",
  "ri-team-line",
  "ri-book-open-line",
  "ri-first-aid-kit-line",
  "ri-eye-line",
  "ri-file-list-3-line",
  "ri-moon-clear-line",
];

type StatForm = { value: string; label: string; icon: string; sort_order: number };

const emptyForm: StatForm = {
  value: "",
  label: "",
  icon: "ri-heart-3-line",
  sort_order: 0,
};

export default function AdminIndicateurs() {
  const { stats, loading, error, reload } = useStats();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<StatForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [isError, setIsError] = useState(false);

  function startAdd() {
    setEditingId(null);
    setForm({ ...emptyForm, sort_order: stats.length });
    setShowForm(true);
    setMsg("");
  }

  function startEdit(stat: Stat) {
    setEditingId(stat.id);
    setForm({
      value: stat.value,
      label: stat.label,
      icon: stat.icon,
      sort_order: stat.sort_order,
    });
    setShowForm(true);
    setMsg("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMsg("");
    setIsError(false);

    let err = null;
    if (editingId) {
      const res = await supabase.from("stats").update(form).eq("id", editingId);
      err = res.error;
    } else {
      const res = await supabase.from("stats").insert(form);
      err = res.error;
    }

    setSaving(false);
    if (err) {
      setMsg(err.message);
      setIsError(true);
      return;
    }

    setMsg(editingId ? "Indicateur modifié." : "Indicateur ajouté.");
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
    reload();
  }

  async function handleDelete(id: string) {
    const { error: err } = await supabase.from("stats").delete().eq("id", id);
    if (err) {
      setMsg(err.message);
      setIsError(true);
      return;
    }
    reload();
  }

  return (
    <div className="p-6 md:p-10">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Chiffres clés
          </h1>
          <p className="mt-2 text-sm md:text-base text-foreground-600">
            Les indicateurs affichés sur la page d'accueil.
          </p>
        </div>
        <button
          type="button"
          onClick={startAdd}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-5 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer"
        >
          <i className="ri-add-line"></i>
          Ajouter un indicateur
        </button>
      </div>

      {(msg || error) && (
        <div
          className={`mt-6 rounded-md px-4 py-3 text-sm ${
            isError
              ? "bg-accent-100 text-accent-700"
              : error
              ? "bg-primary-100 text-primary-700"
              : "bg-primary-100 text-primary-700"
          }`}
        >
          {isError ? msg : error || msg}
        </div>
      )}

      {showForm && (
        <form
          onSubmit={handleSave}
          className="mt-6 bg-background-50 rounded-lg border border-background-200 p-6 md:p-8 space-y-4"
        >
          <h3 className="font-heading text-base font-semibold text-foreground-950">
            {editingId ? "Modifier l'indicateur" : "Nouvel indicateur"}
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Valeur (ex: 4 850)
              </label>
              <input
                type="text"
                required
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Libellé (ex: Personnes aidées)
              </label>
              <input
                type="text"
                required
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Icône
              </label>
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-md bg-accent-100 text-accent-600">
                  <i className={`${form.icon} text-lg`}></i>
                </span>
                <select
                  value={form.icon}
                  onChange={(e) => setForm({ ...form, icon: e.target.value })}
                  className={inputClass}
                >
                  {iconOptions.map((icon) => (
                    <option key={icon} value={icon}>
                      {icon.replace("ri-", "").replace("-line", "")}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground-700 mb-1.5">
                Ordre d'affichage
              </label>
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) =>
                  setForm({ ...form, sort_order: Number(e.target.value) || 0 })
                }
                className={inputClass}
              />
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 px-6 py-2.5 text-sm font-semibold transition-all duration-200 cursor-pointer disabled:opacity-60"
            >
              {saving ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  Enregistrement…
                </>
              ) : (
                "Enregistrer"
              )}
            </button>
            <button
              type="button"
              onClick={() => setShowForm(false)}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 px-6 py-2.5 text-sm font-semibold transition cursor-pointer"
            >
              Annuler
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="mt-10 flex items-center justify-center">
          <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
        </div>
      ) : (
        <div className="mt-8 bg-background-50 rounded-lg border border-background-200 divide-y divide-background-200">
          {stats.length === 0 ? (
            <p className="p-8 text-sm text-foreground-500">
              Aucun indicateur pour le moment. Cliquez sur « Ajouter un
              indicateur ».
            </p>
          ) : (
            stats.map((stat) => (
              <div
                key={stat.id}
                className="flex items-center gap-4 p-4 md:p-5"
              >
                <span className="w-11 h-11 shrink-0 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                  <i className={`${stat.icon} text-lg`}></i>
                </span>
                <div className="flex-1 min-w-0">
                  <p className="font-heading text-base font-bold text-foreground-950">
                    {stat.value}
                  </p>
                  <p className="text-sm text-foreground-600">{stat.label}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => startEdit(stat)}
                    aria-label="Modifier"
                    className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-background-100 hover:text-primary-600 transition cursor-pointer"
                  >
                    <i className="ri-pencil-line"></i>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(stat.id)}
                    aria-label="Supprimer"
                    className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition cursor-pointer"
                  >
                    <i className="ri-delete-bin-line"></i>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}